import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Calculator,
  Flame,
  Utensils, 
  Dumbbell,
  Calendar,
  Target,
  ArrowRight,
  LineChart,
  Info
} from "lucide-react";
import api from "services/api";
import useStore from "services/store";
import toast from "react-hot-toast";

export function HomeDashboard() {
  const navigate = useNavigate();
  const { user, metrics, setMetrics } = useStore();
  const [showInfo, setShowInfo] = useState({
    bmi: false,
    bmr: false,
    tdee: false,
    calories: false
  });

  useEffect(() => {
      getUserMetrics();
  }, []);

  const getUserMetrics = async () => {
    try {
      const response = await api.get("/user/metrics");
      if (!response.ok) {
        toast.error("Unable to retrieve metrics");
        return;
      }
      setMetrics(response.data)
    } catch (error) {
      toast.error("Unable to retrieve metrics");
      return;
    }
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { category: "Underweight", color: "text-red-500" };
    if (bmi < 25) return { category: "Normal", color: "text-green-500" };
    if (bmi < 30) return { category: "Overweight", color: "text-yellow-500" };
    if (bmi >= 30) return { category: "Obese", color: "text-red-500" };
    return { category: "No data", color: "text-gray-500" };
  };

  const bmiInfo = getBMICategory(metrics?.bmi);

  const toggleInfo = (metric) => {
    setShowInfo(prev => ({
      ...prev,
      [metric]: !prev[metric]
    }));
  };

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-black mb-2">
            Welcome back, {user.first_name}!
          </h1>
          <p className="text-gray-600">Here's your fitness overview</p>
        </div>

        {/* User Details Card */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold text-black mb-4 flex items-center">
            <User className="mr-2 text-blue-500" size={20} />
            Your Fitness Profile
          </h2>

          {/* Main Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <MetricCard
              title="BMI"
              value={metrics?.bmi?.toFixed(1) || 'N/A'}
              icon={<Calculator size={20} />}
              color="text-purple-500"
              subtitle="Body Mass Index"
              category={bmiInfo}
              showInfo={showInfo.bmi}
              onInfoClick={() => toggleInfo('bmi')}
              infoText="Body Mass Index (BMI) is a measure of body fat based on your height and weight. It helps assess if you are at a healthy weight."
            />
            <MetricCard
              title="BMR"
              value={metrics?.bmr.toFixed(0) || 'N/A'}
              unit="calories"
              icon={<Flame size={20} />}
              color="text-orange-500"
              subtitle="Basal Metabolic Rate"
              showInfo={showInfo.bmr}
              onInfoClick={() => toggleInfo('bmr')}
              infoText="Basal Metabolic Rate (BMR) is the number of calories your body burns at rest to maintain basic vital functions."
            />
            <MetricCard
              title="TDEE"
              value={metrics?.tdee.toFixed(0) || 'N/A'}
              unit="calories"
              icon={<Flame size={20} />}
              color="text-red-500"
              subtitle="Total Daily Energy Expenditure"
              showInfo={showInfo.tdee}
              onInfoClick={() => toggleInfo('tdee')}
              infoText="Total Daily Energy Expenditure (TDEE) is the total number of calories you burn in a day, including activity and exercise."
            />
            <MetricCard
              title="Daily Calories"
              value={metrics?.daily_calories.toFixed(0) || 'N/A'}
              unit="calories"
              icon={<Target size={20} />}
              color="text-green-500"
              subtitle="Target Daily Intake"
              showInfo={showInfo.calories}
              onInfoClick={() => toggleInfo('calories')}
              infoText="Your recommended daily caloric intake based on your goals, whether you want to gain, maintain, or lose weight."
            />
          </div>

          {/* Macro Distribution */}
          <div className="mb-4">
            <h3 className="text-md font-semibold text-black mb-3">
              Daily Macro Goals
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <MacroCard
                title="Protein"
                value={metrics?.macro_distribution?.protein}
                unit="g"
                color="text-blue-500"
              />
              <MacroCard
                title="Carbs"
                value={metrics?.macro_distribution?.carbs}
                unit="g"
                color="text-yellow-500"
              />
              <MacroCard
                title="Fats"
                value={metrics?.macro_distribution?.fats}
                unit="g"
                color="text-red-500"
              />
            </div>
          </div>

          {/* Goal Progress */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-2">
                  <Target className="text-indigo-500 mr-2" size={20} />
                  <span className="text-sm font-medium text-black">
                    Progress Towards Your Weight Goal
                  </span>
                </div>
                <div className="text-xs text-gray-600">
                  Current: {user.current_weight || 'N/A'} kg → Target: {user.target_weight || 'N/A'} kg
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-black">
                  {metrics?.estimated_time_to_goal === 0 ? '0' : metrics?.estimated_time_to_goal || 'N/A'} weeks
                </div>
                <div className="text-xs text-gray-500">Remaining</div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <NavigationCard
            title="Meal Dashboard"
            description="View your meal plans, track your nutrition and manage your diet"
            icon={<Utensils size={24} />}
            color="text-green-500"
            onClick={() => navigate("/meal-dashboard")}
          />
          <NavigationCard
            title="Exercise Dashboard"
            description="Check your workout plans, track your exercises and monitor your progress"
            icon={<Dumbbell size={24} />}
            color="text-blue-500"
            onClick={() => navigate("/exercise-dashboard")}
          />
          <NavigationCard
            title="Progress Dashboard"
            description="Track your progress, visualize your evolution and monitor your advancement"
            icon={<LineChart size={24} />}
            color="text-purple-500"
            onClick={() => navigate("/progress-dashboard")}
          />
        </div>

        {/* Quick Stats */}
        <div className="mt-8 bg-white rounded-lg p-8 text-center border border-gray-200 shadow-sm">
          <div className="text-black text-4xl mb-4">
            <Calendar className="mx-auto" size={48} />
          </div>
          <h3 className="text-xl font-semibold text-black mb-2">Coming Soon!</h3>
          <p className="text-black mb-4">
            We're working hard to bring you weekly statistics. Stay tuned for updates!
          </p>
          <div className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-full text-sm font-medium">
            <Calendar className="mr-2" size={16} />
            In Development
          </div>
        </div>
      </div>
    </div>
  );
}

const MetricCard = ({ title, value, unit, icon, color, subtitle, category, showInfo, onInfoClick, infoText }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 relative">
    <div className="flex items-center justify-between mb-2">
      <div className={`text-2xl ${color}`}>{icon}</div>
      <div className="text-right">
        <div className="text-lg font-bold text-black">{value}</div>
        {unit && <div className="text-sm text-gray-600">{unit}</div>}
      </div>
    </div>
    <div className="flex items-center">
      <div className="text-xs text-gray-500">{title}</div>
      <button 
        onClick={onInfoClick}
        className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200 ml-1"
      >
        <Info size={12} className="text-gray-500" />
      </button>
      {showInfo && (
        <div className="absolute left-full ml-2 p-3 bg-white rounded-lg shadow-lg border border-gray-200 z-10 w-48">
          <p className="text-xs text-gray-600">{infoText}</p>
        </div>
      )}
    </div>
    {subtitle && <div className="text-xs text-gray-400 mt-1">{subtitle}</div>}
    {category && <div className={`text-xs ${category.color} mt-1`}>{category.category}</div>}
  </div>
);

const MacroCard = ({ title, value, unit, color }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
    <div className="text-center">
      <div className={`text-2xl font-bold ${color}`}>{value || 'N/A'}</div>
      <div className="text-sm text-gray-600">{unit}</div>
      <div className="text-xs text-gray-500 mt-1">{title}</div>
    </div>
  </div>
);

const NavigationCard = ({ title, description, icon, color, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <div className={`text-3xl ${color} mr-4`}>{icon}</div>
        <div>
          <h3 className="text-lg font-semibold text-black">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
      <div
        className={`text-xl ${color} group-hover:translate-x-1 transition-transform duration-200`}
      >
        <ArrowRight size={24} />
      </div>
    </div>
  </div>
);
